/**
 * External Script Loading Utility
 * Ensures external scripts are loaded before components try to use them
 */

class ScriptLoader {
  constructor() {
    this.loadedScripts = new Set()
    this.loadingPromises = new Map()
    this.retryAttempts = new Map()
    this.maxRetries = 3
  }

  /**
   * Check if a global variable exists and is ready
   */
  isScriptReady(scriptName, globalVar) {
    return typeof window !== 'undefined' && 
           window[globalVar] && 
           typeof window[globalVar] === 'function'
  }

  /**
   * Wait for a script to be available with retry logic
   */
  async waitForScript(scriptName, globalVar, timeout = 10000, retryInterval = 100) {
    return new Promise((resolve, reject) => {
      let elapsed = 0
      let retryCount = 0
      
      const check = () => {
        if (this.isScriptReady(scriptName, globalVar)) {
          console.log(`✅ ${scriptName} is ready (${globalVar} available)`)
          resolve(window[globalVar])
          return
        }

        elapsed += retryInterval
        retryCount++

        if (elapsed >= timeout) {
          console.error(`❌ ${scriptName} failed to load within ${timeout}ms after ${retryCount} attempts`)
          reject(new Error(`${scriptName} loading timeout`))
          return
        }

        // Exponential backoff for retries
        const delay = Math.min(retryInterval * Math.pow(1.5, Math.floor(retryCount / 10)), 1000)
        setTimeout(check, delay)
      }

      console.log(`⏳ Waiting for ${scriptName} (${globalVar})...`)
      check()
    })
  }

  /**
   * Enhanced Swiper loader with comprehensive error handling
   */
  async loadSwiper() {
    const scriptName = 'Swiper'
    const globalVar = 'Swiper'
    
    if (this.loadingPromises.has(scriptName)) {
      return this.loadingPromises.get(scriptName)
    }

    if (this.isScriptReady(scriptName, globalVar)) {
      return Promise.resolve(window[globalVar])
    }

    const loadPromise = this.waitForScript(scriptName, globalVar, 15000, 100)
      .catch(async (error) => {
        console.warn(`⚠️ ${scriptName} not loaded via CDN, attempting fallback...`)
        
        // Fallback: Try to load from alternative CDN
        const fallbackScript = document.createElement('script')
        fallbackScript.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js'
        fallbackScript.async = true
        fallbackScript.crossOrigin = 'anonymous'
        
        document.head.appendChild(fallbackScript)
        
        // Wait for fallback
        return this.waitForScript(scriptName, globalVar, 10000, 200)
      })

    this.loadingPromises.set(scriptName, loadPromise)
    return loadPromise
  }

  /**
   * Load YouTube API
   */
  async loadYouTubeAPI() {
    const scriptName = 'YouTube API'
    const globalVar = 'YT'
    
    if (this.loadingPromises.has(scriptName)) {
      return this.loadingPromises.get(scriptName)
    }

    if (window.YT && window.YT.Player) {
      return Promise.resolve(window.YT)
    }

    const loadPromise = new Promise((resolve, reject) => {
      // Set up the callback for when YouTube API is ready
      window.onYouTubeIframeAPIReady = () => {
        console.log('✅ YouTube API is ready')
        resolve(window.YT)
      }

      // Check if API is already loaded
      if (window.YT && window.YT.Player) {
        resolve(window.YT)
        return
      }

      // Timeout fallback
      setTimeout(() => {
        if (!window.YT || !window.YT.Player) {
          console.error('❌ YouTube API failed to load')
          reject(new Error('YouTube API loading timeout'))
        }
      }, 10000)
    })

    this.loadingPromises.set(scriptName, loadPromise)
    return loadPromise
  }

  /**
   * Initialize all critical scripts
   */
  async initializeAllScripts() {
    console.log('🚀 Initializing external scripts...')
    
    const results = await Promise.allSettled([
      this.loadSwiper(),
      this.loadYouTubeAPI()
    ])

    const loadedScripts = []
    const failedScripts = []

    results.forEach((result, index) => {
      const scriptNames = ['Swiper', 'YouTube API']
      if (result.status === 'fulfilled') {
        loadedScripts.push(scriptNames[index])
      } else {
        failedScripts.push({
          name: scriptNames[index],
          error: result.reason
        })
      }
    })

    console.log('✅ Loaded scripts:', loadedScripts)
    if (failedScripts.length > 0) {
      console.error('❌ Failed scripts:', failedScripts)
    }

    return {
      success: loadedScripts,
      failed: failedScripts,
      allLoaded: failedScripts.length === 0
    }
  }

  /**
   * Get loading status
   */
  getStatus() {
    return {
      swiper: this.isScriptReady('Swiper', 'Swiper'),
      youtube: window.YT && window.YT.Player,
      loaded: Array.from(this.loadedScripts),
      loading: Array.from(this.loadingPromises.keys())
    }
  }
}

// Create singleton instance
export const scriptLoader = new ScriptLoader()

// Initialize on module load
if (typeof window !== 'undefined') {
  // Auto-initialize after a short delay to allow HTML scripts to load
  setTimeout(() => {
    scriptLoader.initializeAllScripts().catch(console.error)
  }, 1000)
}

export default ScriptLoader
